import {
    BodyText,
    Button,
    Checkbox,
    Eyebrow,
    Form,
    Headline,
    Icon,
    Stack,
} from '@servicetitan/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { usePortalMode } from '../../../hooks/use-portal-mode';
import {
    isGroupedOptions,
    SelectFieldState,
    SelectOption,
    SelectOptionGroup,
} from '../../../utils/form';
import { PortalPopover, PortalPopoverProps } from '../../modals/modals';
import {
    PortalSelectTriggerInput,
    PortalSelectTriggerTag,
    PortalSelectTriggerOpts,
    PortalSelectTriggerProps,
} from './select-triggers';
import * as Styles from './select.module.less';

export interface PortalSelectSearchProps {
    className?: string;
    allText?: string;
    noneText?: string;
    saveText?: string;
    placeholder?: string;
}

export interface PortalSelectProps<T, TD = never> {
    className?: string;
    title: string;
    mobile: boolean;
    disabled?: boolean;
    state: SelectFieldState<T, T | T[], TD>;
    trigger?: PortalSelectTriggerOpts;
    triggerComponent?: 'tag' | FC<PortalSelectTriggerProps<T, TD>>;
    popover?: PortalPopoverProps['popover'];
    takeover?: PortalPopoverProps['takeover'];
    hideSearch?: boolean;
    search?: PortalSelectSearchProps;
    optionComponent?: FC<{ opt: SelectOption<T, TD> }>;
    optionsFilter?(search: string): (opt: SelectOption<T, TD>) => boolean;

    /**
     * triggered when values are changed
     * in mobile mode it happens when user clicks apply button
     * in desktop mode it happens immediately when item checkbox is toggled
     */
    onValuesChanged?(values: T[]): void;

    /**
     * triggered when values are applied
     * in mobile mode it happens when user clicks apply button and there are some changes in selected values
     * in desktop mode it happens when popover is closed and there are some changes in selected values
     */
    onValuesApplied?(values: T[]): void;
}

const SearchInput: FC<{
    className?: string;
    placeholder?: string;
    search: string;
    setSearch(val: string): void;
}> = ({ className, placeholder, search, setSearch }) => (
    <Form.Input
        value={search}
        className={classNames(Styles.inputSearch, className)}
        icon={search ? { name: 'close', size: 16, onClick: () => setSearch('') } : undefined}
        onChange={(e: any) => setSearch(e.target?.value ?? '')}
        shortLabel={<Icon name="search" />}
        placeholder={placeholder}
    />
);

const DefaultOptionComponent: FC<{ opt: SelectOption<any> }> = ({ opt }) => (
    <BodyText>{opt.text}</BodyText>
);
const defaultOptionsFilter = (search: string) => (opt: SelectOption<any>) =>
    opt.text.toLowerCase().includes(search.toLowerCase());

function SearchList<T extends number | string, O extends SelectOption<T>>({
    className,
    mobile,
    multiple,
    optionComponent,
    options,
    optionsFilter,
    opts: { allText = 'All', noneText = 'None' },
    search,
    selectAllValues,
    selectedValues,
    updateSelectedValues,
}: {
    className?: string;
    opts: PortalSelectSearchProps;
    search: string;
    options: O[] | SelectOptionGroup<O>[];
    mobile?: boolean;
    multiple?: boolean;
    selectedValues: T[];
    optionComponent?: FC<{ opt: O }>;
    optionsFilter?(search: string): (opt: O) => boolean;
    updateSelectedValues(v: T[]): void;
    selectAllValues(): void;
}) {
    const filter = useMemo(() => {
        const searchLower = search.trim();

        return searchLower ? (optionsFilter ?? defaultOptionsFilter)(searchLower) : undefined;
    }, [optionsFilter, search]);
    const OptionComponent = optionComponent ?? DefaultOptionComponent;
    const data = useMemo(() => {
        if (!options.length) {
            return {
                groups: [],
                grouped: false,
                hasAny: false,
            };
        }

        let groupedOptions: SelectOptionGroup<O>[];
        let isGrouped = false;
        if (isGroupedOptions(options)) {
            groupedOptions = filter
                ? options
                      .map(group => ({
                          text: group.text,
                          options: group.options.filter(filter),
                      }))
                      .filter(group => !!group.options.length)
                : options;
            isGrouped = true;
        } else {
            groupedOptions = [
                {
                    text: '',
                    options: filter ? options.filter(filter) : options,
                },
            ].filter(group => !!group.options.length);
        }

        return {
            groups: groupedOptions,
            grouped: isGrouped,
            hasAny: !!options.length,
        };
    }, [options, filter]);

    const toggleValue = (val: T) => {
        if (multiple) {
            updateSelectedValues(
                selectedValues.includes(val)
                    ? selectedValues.filter(v => v !== val)
                    : [...selectedValues, val]
            );
        } else {
            updateSelectedValues([val]);
        }
    };
    const selectNoneValues = () => {
        updateSelectedValues([]);
    };
    const isSelectedAll = data.groups.every(gr =>
        gr.options.every(opt => selectedValues.includes(opt.value))
    );

    return (
        <Stack
            className={classNames(
                className,
                mobile ? Styles.searchItemContainerTakeover : Styles.searchItemContainerPopover
            )}
            direction="column"
        >
            {mobile && multiple && !!data.groups.length && (
                <Stack
                    className={Styles.searchItem}
                    onClick={isSelectedAll ? selectNoneValues : selectAllValues}
                >
                    <Stack.Item>
                        <Checkbox checked={isSelectedAll} />
                    </Stack.Item>
                    <Stack.Item fill className="m-t-half m-l-1">
                        {isSelectedAll ? noneText : allText}
                    </Stack.Item>
                </Stack>
            )}
            {data.groups.map((group, ind) => (
                <Fragment key={`${group.text}${ind.toString()}`}>
                    {data.grouped && (
                        <Eyebrow className={Styles.searchGroupTitle}>{group.text}</Eyebrow>
                    )}

                    {group.options.map(opt => (
                        <Stack
                            key={opt.value}
                            className={classNames(Styles.searchItem, {
                                [Styles.searchItemActive]:
                                    !multiple && selectedValues.includes(opt.value),
                            })}
                            onClick={() => toggleValue(opt.value)}
                        >
                            {multiple && (
                                <Checkbox
                                    className="m-r-1"
                                    checked={selectedValues.includes(opt.value)}
                                />
                            )}
                            <Stack.Item fill>
                                <OptionComponent opt={opt} />
                            </Stack.Item>
                        </Stack>
                    ))}
                </Fragment>
            ))}

            {data.groups.length === 0 && (
                <BodyText subdued className="w-100 ta-center p-y-3">
                    {data.hasAny ? 'No items found' : 'No options found'}
                </BodyText>
            )}
        </Stack>
    );
}

/**
 * Replica of AnvilSelect extended for mobile usage
 * @param props PortalSelectProps
 */
export function PortalSelect<T extends number | string, TD>({
    className,
    disabled,
    hideSearch,
    mobile,
    onValuesApplied,
    onValuesChanged,
    optionComponent,
    optionsFilter,
    popover = {},
    search: searchOpts = {},
    state,
    takeover = {},
    title,
    trigger,
    triggerComponent,
}: PortalSelectProps<T, TD>) {
    const [searchOpen, setSearchOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedValues, setSelectedValues] = useState<T[]>([]);
    const [initialSelectedValues, setInitialSelectedValues] = useState<T[]>([]);
    const multiple = state.multiple;
    const areValuesChanged = useMemo(
        () =>
            selectedValues.some(i => !initialSelectedValues.includes(i)) ||
            initialSelectedValues.some(i => !selectedValues.includes(i)),
        [selectedValues, initialSelectedValues]
    );
    const closeSearch = useCallback(() => {
        setSearchOpen(false);

        if (onValuesApplied && areValuesChanged) {
            onValuesApplied(selectedValues);
        }
    }, [selectedValues, areValuesChanged, onValuesApplied]);
    const openSearch = useCallback(() => {
        if (disabled) {
            return;
        }
        const values = state.selectedOptions?.map(opt => opt.value) ?? [];
        setSelectedValues([...values]);
        setInitialSelectedValues([...values]);
        setSearch('');
        setSearchOpen(true);
    }, [state.selectedOptions, disabled]);
    const onChangeStateValues = useCallback(
        (values: T[]) => {
            state.onChangeHandler(
                values.length
                    ? values.map(v => ({ value: v, text: '', data: undefined as any }))
                    : undefined
            );

            if (onValuesChanged) {
                onValuesChanged(values);
            }
        },
        [onValuesChanged, state]
    );
    const onApply = useCallback(() => {
        onChangeStateValues(selectedValues);
        closeSearch();
    }, [closeSearch, onChangeStateValues, selectedValues]);
    const updateSelectedValues = useCallback(
        (values: T[]) => {
            setSelectedValues(values);

            if (!mobile || !multiple) {
                onChangeStateValues(values);
            }

            if (!multiple && !mobile) {
                // for mobile mode change value after Save button click
                closeSearch();
            }
        },
        [mobile, multiple, onChangeStateValues, closeSearch]
    );

    const selectAllValues = useCallback(() => {
        updateSelectedValues(state.flatOptions.map(opt => opt.value));
    }, [state, updateSelectedValues]);

    const selectNoneValues = useCallback(() => {
        updateSelectedValues([]);
    }, [updateSelectedValues]);

    const TriggerComponent =
        triggerComponent === 'tag'
            ? PortalSelectTriggerTag
            : (triggerComponent ?? PortalSelectTriggerInput);
    const triggerValue = (
        <TriggerComponent
            title={trigger?.title ?? title}
            opts={trigger}
            selectedOptions={state.selectedOptions as any}
            searchOpen={searchOpen}
            openSearch={openSearch}
            closeSearch={closeSearch}
            updateValues={updateSelectedValues}
            multiple={multiple}
            disabled={disabled}
        />
    );

    const { placeholder: searchPlaceholder = 'Search', saveText = 'Save' } = searchOpts;

    return (
        <div className={classNames(className)}>
            <PortalPopover
                title={title}
                mobile={mobile}
                trigger={triggerValue}
                onClose={closeSearch}
                popover={popover}
                takeover={{
                    theme: 'combined',
                    ...(takeover ?? {}),
                }}
                open={searchOpen}
                footerAlign={mobile ? 'space-between' : 'left'}
                thin
                footer={
                    mobile ? (
                        <Fragment>
                            <Button
                                className="flex-grow-1 flex-basis-0 m-r-1"
                                onClick={selectNoneValues}
                            >
                                Clear All
                            </Button>
                            <Button
                                className="flex-grow-1 flex-basis-0 m-l-1"
                                onClick={onApply}
                                disabled={!areValuesChanged}
                                primary
                            >
                                {saveText}
                                {multiple && selectedValues.length
                                    ? ` (${
                                          selectedValues.length === state.flatOptions.length
                                              ? 'All'
                                              : selectedValues.length
                                      })`
                                    : ''}
                            </Button>
                        </Fragment>
                    ) : multiple ? (
                        <Fragment>
                            <BodyText size="small" subdued>
                                Select
                            </BodyText>
                            <BodyText
                                size="small"
                                className="c-blue-500 cursor-pointer m-l-1"
                                onClick={selectAllValues}
                            >
                                All
                            </BodyText>
                            <BodyText
                                size="small"
                                className="c-blue-500 cursor-pointer m-l-1"
                                onClick={selectNoneValues}
                            >
                                None
                            </BodyText>
                        </Fragment>
                    ) : undefined
                }
            >
                {!mobile && !!title && (
                    <Headline className="m-t-2 m-x-3" size="small">
                        {title}
                    </Headline>
                )}
                {!hideSearch && (
                    <SearchInput
                        className={classNames(
                            'p-b-3 m-t-2 m-b-0',
                            mobile ? 'p-x-2' : 'p-x-3 border-bottom'
                        )}
                        search={search}
                        setSearch={setSearch}
                        placeholder={searchPlaceholder}
                    />
                )}
                <SearchList
                    search={search}
                    options={state.options}
                    selectedValues={selectedValues}
                    updateSelectedValues={updateSelectedValues}
                    selectAllValues={selectAllValues}
                    optionComponent={optionComponent}
                    optionsFilter={optionsFilter}
                    multiple={multiple}
                    mobile={mobile}
                    opts={searchOpts}
                />
            </PortalPopover>
        </div>
    );
}

function PortalSelectObserverContent<T extends number | string, TD>(
    props: Omit<PortalSelectProps<T, TD>, 'mobile'>
) {
    const mode = usePortalMode();

    return <PortalSelect mobile={mode.mobile} {...props} />;
}

/**
 * Replica of AnvilSelect extended for mobile usage
 * @param props PortalSelectProps
 */
export const PortalSelectObserver = observer(PortalSelectObserverContent);
